/**
 * Registry of all supported studies.
 *
 * Each entry knows:
 *  - id           : stable string used in URL / persistence / Chart state
 *  - label        : display string (matches the screenshot exactly)
 *  - category     : the popover section it appears in
 *  - placement    : 'price' (overlay) or 'sub' (own oscillator pane)
 *  - color        : hex used for line/bar rendering
 *  - compute      : pure function (candles → numeric series)
 *
 * Multi-line indicators (MACD, ADX, Bollinger, Stochastic, Aroon) declare
 * extra series via `extra` so the Chart can render them in the same pane.
 */
import {
  type Candle,
  sma, ema, dema, tema, mcginley, bollinger, vwap, priceChannel, linearRegression,
  rsi, macd, stochastic, cci, adx, awesomeOscillator,
  moneyFlowIndex, accDistribution, chaikinMoneyFlow, chaikinOscillator,
  forceIndex, onBalanceVolume, negVolumeIndex, posVolumeIndex, aroon, volumeAvg,
} from './studies';

export type StudyId =
  | 'sma20' | 'sma50' | 'sma200' | 'ema12' | 'ema26' | 'bb' | 'vwap' | 'pc' | 'lr'
  | 'volume' | 'rsi14' | 'macd' | 'adx14' | 'cci20' | 'stoch' | 'ao'
  | 'ad' | 'aroon' | 'cmf' | 'cho' | 'mfi' | 'obv' | 'force' | 'volavg' | 'nvi' | 'pvi'
  | 'dema20' | 'tema20' | 'mcg';

export type StudyCategory = 'Popular' | 'Market Strength' | 'Trend';
export type Placement = 'price' | 'sub';

export interface StudyLine { name: string; values: number[]; color: string; dashed?: boolean; thickness?: number; }
export interface StudyResult { lines: StudyLine[]; histogram?: { values: number[]; color: string }; domain?: [number, number]; zeroLine?: boolean; }

export interface StudyDef {
  id: StudyId;
  label: string;
  category: StudyCategory;
  placement: Placement;
  /** Compact label shown in the Chart subpane header. */
  short: string;
  compute: (candles: Candle[]) => StudyResult;
}

const PURPLE = '#a78bfa';
const RED = '#f87171';
const AMBER = '#fbbf24';
const CYAN = '#3dd6ff';
const GREEN = '#3ddc84';
const PINK = '#ec4899';
const TEAL = '#2dd4bf';
const ORANGE = '#fb923c';
const SLATE = '#94a3b8';

export const STUDY_REGISTRY: StudyDef[] = [
  // ===== Popular =====
  { id: 'sma20', label: 'SMA (20)', short: 'SMA20', category: 'Popular', placement: 'price',
    compute: (c) => ({ lines: [{ name: 'SMA(20)', values: sma(c.map(x => x.close), 20), color: AMBER }] }) },
  { id: 'sma50', label: 'SMA (50)', short: 'SMA50', category: 'Popular', placement: 'price',
    compute: (c) => ({ lines: [{ name: 'SMA(50)', values: sma(c.map(x => x.close), 50), color: ORANGE }] }) },
  { id: 'sma200', label: 'SMA (200)', short: 'SMA200', category: 'Popular', placement: 'price',
    compute: (c) => ({ lines: [{ name: 'SMA(200)', values: sma(c.map(x => x.close), 200), color: PINK }] }) },
  { id: 'ema12', label: 'EMA (12)', short: 'EMA12', category: 'Popular', placement: 'price',
    compute: (c) => ({ lines: [{ name: 'EMA(12)', values: ema(c.map(x => x.close), 12), color: CYAN }] }) },
  { id: 'ema26', label: 'EMA (26)', short: 'EMA26', category: 'Popular', placement: 'price',
    compute: (c) => ({ lines: [{ name: 'EMA(26)', values: ema(c.map(x => x.close), 26), color: TEAL }] }) },
  { id: 'bb', label: 'Bollinger Bands', short: 'BB(20,2)', category: 'Popular', placement: 'price',
    compute: (c) => {
      const b = bollinger(c.map(x => x.close), 20, 2);
      return { lines: [
        { name: 'BB Upper', values: b.upper, color: PURPLE, dashed: true },
        { name: 'BB Mid',   values: b.mid,   color: PURPLE, thickness: .8 },
        { name: 'BB Lower', values: b.lower, color: PURPLE, dashed: true },
      ]};
    }},
  { id: 'vwap', label: 'VWAP', short: 'VWAP', category: 'Popular', placement: 'price',
    compute: (c) => ({ lines: [{ name: 'VWAP', values: vwap(c), color: GREEN, thickness: 1.4 }] }) },
  { id: 'pc', label: 'Price Channel', short: 'PC(20)', category: 'Popular', placement: 'price',
    compute: (c) => {
      const p = priceChannel(c, 20);
      return { lines: [
        { name: 'Channel High', values: p.upper, color: SLATE },
        { name: 'Channel Low',  values: p.lower, color: SLATE },
      ]};
    }},
  { id: 'lr', label: 'Linear Regression', short: 'LR(20)', category: 'Popular', placement: 'price',
    compute: (c) => ({ lines: [{ name: 'LinReg(20)', values: linearRegression(c.map(x => x.close), 20), color: PINK }] }) },

  { id: 'volume', label: 'Volume', short: 'Vol', category: 'Popular', placement: 'sub',
    compute: (c) => ({
      lines: [],
      histogram: { values: c.map(x => x.volume), color: GREEN },
    }) },
  { id: 'rsi14', label: 'RSI (14)', short: 'RSI(14)', category: 'Popular', placement: 'sub',
    compute: (c) => ({ lines: [{ name: 'RSI(14)', values: rsi(c.map(x => x.close), 14), color: PINK }], domain: [0, 100] }) },
  { id: 'macd', label: 'MACD', short: 'MACD(12,26,9)', category: 'Popular', placement: 'sub',
    compute: (c) => {
      const m = macd(c.map(x => x.close), 12, 26, 9);
      return {
        lines: [
          { name: 'MACD',   values: m.macd,   color: CYAN, thickness: 1.2 },
          { name: 'Signal', values: m.signal, color: AMBER, thickness: 1.2 },
        ],
        histogram: { values: m.histogram, color: GREEN },
        zeroLine: true,
      };
    }},
  { id: 'adx14', label: 'ADX (14)', short: 'ADX(14)', category: 'Popular', placement: 'sub',
    compute: (c) => {
      const a = adx(c, 14);
      return { lines: [
        { name: 'ADX', values: a.adx, color: AMBER, thickness: 1.2 },
        { name: '+DI', values: a.pdi, color: GREEN },
        { name: '-DI', values: a.mdi, color: RED },
      ], domain: [0, 100] };
    }},
  { id: 'cci20', label: 'CCI (20)', short: 'CCI(20)', category: 'Popular', placement: 'sub',
    compute: (c) => ({ lines: [{ name: 'CCI(20)', values: cci(c, 20), color: CYAN }], zeroLine: true }) },
  { id: 'stoch', label: 'Stochastics', short: 'Stoch', category: 'Popular', placement: 'sub',
    compute: (c) => {
      const s = stochastic(c, 14, 3, 3);
      return { lines: [
        { name: '%K', values: s.k, color: CYAN },
        { name: '%D', values: s.d, color: AMBER, dashed: true },
      ], domain: [0, 100] };
    }},
  { id: 'ao', label: 'Awesome Oscillator', short: 'AO', category: 'Popular', placement: 'sub',
    compute: (c) => ({ lines: [], histogram: { values: awesomeOscillator(c), color: AMBER }, zeroLine: true }) },

  // ===== Market Strength =====
  { id: 'ad', label: 'Acc/Distribution', short: 'A/D', category: 'Market Strength', placement: 'sub',
    compute: (c) => ({ lines: [{ name: 'A/D', values: accDistribution(c), color: CYAN }] }) },
  { id: 'aroon', label: 'Aroon Up/Down', short: 'Aroon(14)', category: 'Market Strength', placement: 'sub',
    compute: (c) => {
      const a = aroon(c, 14);
      return { lines: [
        { name: 'Aroon Up',   values: a.up,   color: GREEN },
        { name: 'Aroon Down', values: a.down, color: RED },
      ], domain: [0, 100] };
    }},
  { id: 'cmf', label: 'Chaikin Money Flow', short: 'CMF(20)', category: 'Market Strength', placement: 'sub',
    compute: (c) => ({ lines: [{ name: 'CMF', values: chaikinMoneyFlow(c, 20), color: TEAL }], zeroLine: true, domain: [-1, 1] }) },
  { id: 'cho', label: 'Chaikin Oscillator', short: 'CHO', category: 'Market Strength', placement: 'sub',
    compute: (c) => ({ lines: [{ name: 'CHO', values: chaikinOscillator(c), color: PURPLE }], zeroLine: true }) },
  { id: 'mfi', label: 'Money Flow Index', short: 'MFI(14)', category: 'Market Strength', placement: 'sub',
    compute: (c) => ({ lines: [{ name: 'MFI(14)', values: moneyFlowIndex(c, 14), color: AMBER }], domain: [0, 100] }) },
  { id: 'obv', label: 'On Balance Volume', short: 'OBV', category: 'Market Strength', placement: 'sub',
    compute: (c) => ({ lines: [{ name: 'OBV', values: onBalanceVolume(c), color: ORANGE }] }) },
  { id: 'force', label: 'Force Index', short: 'Force(13)', category: 'Market Strength', placement: 'sub',
    compute: (c) => ({ lines: [{ name: 'Force(13)', values: forceIndex(c, 13), color: GREEN }], zeroLine: true }) },
  { id: 'volavg', label: 'Volume w/ Avg', short: 'Vol+SMA', category: 'Market Strength', placement: 'sub',
    compute: (c) => {
      const v = volumeAvg(c, 20);
      return { lines: [{ name: 'Vol SMA(20)', values: v.avg, color: AMBER, thickness: 1.2 }],
               histogram: { values: v.volume, color: GREEN } };
    }},
  { id: 'nvi', label: 'Neg Volume Index', short: 'NVI', category: 'Market Strength', placement: 'sub',
    compute: (c) => ({ lines: [{ name: 'NVI', values: negVolumeIndex(c), color: RED }] }) },
  { id: 'pvi', label: 'Pos Volume Index', short: 'PVI', category: 'Market Strength', placement: 'sub',
    compute: (c) => ({ lines: [{ name: 'PVI', values: posVolumeIndex(c), color: GREEN }] }) },

  // ===== Trend =====
  { id: 'dema20', label: 'DEMA (20)', short: 'DEMA(20)', category: 'Trend', placement: 'price',
    compute: (c) => ({ lines: [{ name: 'DEMA(20)', values: dema(c.map(x => x.close), 20), color: TEAL }] }) },
  { id: 'tema20', label: 'TEMA (20)', short: 'TEMA(20)', category: 'Trend', placement: 'price',
    compute: (c) => ({ lines: [{ name: 'TEMA(20)', values: tema(c.map(x => x.close), 20), color: ORANGE }] }) },
  { id: 'mcg', label: 'McGinley Dynamic', short: 'McG', category: 'Trend', placement: 'price',
    compute: (c) => ({ lines: [{ name: 'McGinley', values: mcginley(c.map(x => x.close), 14), color: PINK }] }) },
];

export const STUDIES_BY_ID: Record<StudyId, StudyDef> = Object.fromEntries(
  STUDY_REGISTRY.map((s) => [s.id, s]),
) as Record<StudyId, StudyDef>;

export const STUDY_CATEGORIES: StudyCategory[] = ['Popular', 'Market Strength', 'Trend'];
